import { Avatar } from "@mui/material";
import { useTheme } from "@mui/styles";
import clsx from "clsx";
import { BRAND_PRIMARY_MAIN_COLOR } from "ds_legacy/materials/colors";
import { dp, space } from "ds_legacy/materials/metrics";
import { SHADOW_DP1, SHADOW_DP2 } from "ds_legacy/materials/shadows";
import {
  Badge as BadgeText,
  FONT_SIZE_16,
} from "ds_legacy/materials/typography";
import {
  BellIcon,
  CheckCircleIcon,
  CornerUpRightIcon,
  MailIcon,
  MailOpenIcon,
} from "lucide-react";
import { ReactNode } from "react";
import { useTranslations } from "translations";
import InfoChip from "../InfoChip";

export type BadgeProps = {
  backgroundColor?: string;
  children: ReactNode;
  color?: string;
  large?: boolean;
  offset?: boolean;
  position?: string;
  shadow?: string;
};

type BadgeContainerProps = {
  margin?: string;
  children: ReactNode;
};

export const BadgeContainer = ({ children, margin }: BadgeContainerProps) => (
  <div className="relative" style={{ margin: margin ?? "initial" }}>
    {children}
  </div>
);

type BadgeElementProps = {
  backgroundColor?: string;
  large?: boolean;
  offsetAmount?: boolean;
  position?: string;
  shadow?: string;
  children: ReactNode;
};

export const BadgeElement = ({
  backgroundColor,
  children,
  large,
  offsetAmount,
  position,
  shadow,
}: BadgeElementProps) => {
  const theme = useTheme();

  return (
    <span
      className={clsx(
        "pointer-events-none flex items-center justify-center rounded-full opacity-100",
        {
          absolute: position === "absolute" || !position,
          fixed: position === "fixed",
          relative: position === "relative",
          static: position === "static",
          sticky: position === "sticky",
        },
      )}
      style={{
        backgroundColor: backgroundColor ?? theme.palette.secondary.main,
        boxShadow: shadow,
        height: large ? dp(24) : dp(22),
        right: offsetAmount ? space(-2.5) : space(0),
        top: offsetAmount ? space(-1) : space(0),
        width: large ? dp(24) : dp(22),
      }}
    >
      {children}
    </span>
  );
};

export default function Badge({
  backgroundColor,
  children,
  color,
  large,
  offset,
  position,
  shadow,
}: BadgeProps) {
  if (!children) {
    return null;
  }

  return (
    <BadgeElement
      backgroundColor={backgroundColor}
      large={large}
      offsetAmount={offset}
      position={position}
      shadow={shadow}
    >
      <BadgeText color={color}>{children}</BadgeText>
    </BadgeElement>
  );
}

export function BellBadge({ isHover }: { isHover: boolean | undefined }) {
  const theme = useTheme();
  return (
    <Badge large shadow={isHover ? SHADOW_DP2 : SHADOW_DP1}>
      <BellIcon size={16} style={{ color: theme.palette.common.black }} />
    </Badge>
  );
}

export const NewBadge = ({ marginWrapper }: { marginWrapper?: string }) => {
  const translations = useTranslations();
  return (
    <InfoChip
      color="secondary"
      label={translations.general.new.toUpperCase()}
      margin={marginWrapper}
      variant="filled"
    />
  );
};

export function ProviderSearchRequestBadge() {
  const theme = useTheme();
  return (
    <Avatar
      sx={{
        background: BRAND_PRIMARY_MAIN_COLOR,
        height: 24,
        padding: dp(4),
        width: 24,
      }}
    >
      <CornerUpRightIcon
        data-testid="direct-request-badge"
        size={16}
        style={{ color: theme.palette.common.white, width: "100%" }}
      />
    </Avatar>
  );
}

export const MessageCountBadge = ({
  isActionRequiredByUser,
  label,
  style,
}: {
  isActionRequiredByUser: boolean;
  label: number | string;
  style?: React.CSSProperties;
}) => {
  const icon = isActionRequiredByUser ? (
    <MailIcon size={FONT_SIZE_16} />
  ) : (
    <MailOpenIcon size={FONT_SIZE_16} />
  );
  const color = isActionRequiredByUser ? "secondary" : "primary";
  const variant = isActionRequiredByUser ? "filled" : "outlined";
  return (
    <div style={style}>
      <InfoChip
        color={color}
        icon={icon}
        label={label}
        testId="message-count-badge"
        variant={variant}
      />
    </div>
  );
};

export const AcceptedBadge = ({ label }: { label: number | string }) => {
  const translations = useTranslations();
  return (
    <InfoChip
      color="secondary"
      icon={<CheckCircleIcon size={FONT_SIZE_16} />}
      label={label}
      sx={{ boxShadow: SHADOW_DP1 }}
      tooltip={translations.search.accepted}
    />
  );
};

export const ReverseSearchBadge = ({
  isProviderSearchRequest,
  marginWrapper,
}: {
  isProviderSearchRequest?: boolean;
  marginWrapper?: string;
}) => {
  const translations = useTranslations();
  return (
    <InfoChip
      color={isProviderSearchRequest ? undefined : "primary"}
      label={
        translations.careproviderApp.settings.reversePatientSearch
          .chipBasedOnSearch
      }
      margin={marginWrapper}
      sx={{
        borderColor: isProviderSearchRequest
          ? BRAND_PRIMARY_MAIN_COLOR
          : undefined,
        color: isProviderSearchRequest ? BRAND_PRIMARY_MAIN_COLOR : undefined,
      }}
      variant="outlined"
    />
  );
};
